import type { ReactNode } from 'react';
import {
  Slogan,
  FooterSignature,
  Wordmark,
  StyledFooter,
  Logo,
  LogoLink,
  LogoAnchor,
  List,
  ListItem,
  ListTitle,
  Container,
  ListLink,
  ListAnchor,
} from './Style';
import blackLogo from '../../assets/img/logoSORBlack.svg?url';
import { ArrowUpRight } from '../../../components/ui/icons/ArrowUpRight';

import { useTranslation } from 'react-i18next';
import { useInRouterContext } from 'react-router-dom';

export const Footer = () => {
  const { t, i18n } = useTranslation('common');
  const inRouter = useInRouterContext();

  const language = i18n.language;
  const homePath = language.startsWith('fr') ? '/fr' : '/';

  const FooterLink = ({ to, children }: { to: string; children: ReactNode }) =>
    inRouter ? (
      <ListLink to={to}>{children}</ListLink>
    ) : (
      <ListAnchor href={to}>{children}</ListAnchor>
    );

  const FooterLogoLink = ({
    to,
    children,
  }: {
    to: string;
    children: ReactNode;
  }) =>
    inRouter ? (
      <LogoLink to={to}>{children}</LogoLink>
    ) : (
      <LogoAnchor href={to}>{children}</LogoAnchor>
    );

  return (
    <StyledFooter>
      <FooterSignature>
        <p>
          {language.startsWith('fr')
            ? 'Prenez une longueur d’avance'
            : 'Build your unfair advantage'}
        </p>
        <FooterLink to={t('contactLink')}>
          <span className="sr-only">
            {language.startsWith('fr')
              ? 'Parlons de votre projet'
              : 'Let’s talk about your project'}
          </span>
          <ArrowUpRight />
        </FooterLink>
      </FooterSignature>
      <Container>
        <div>
          <FooterLogoLink to={homePath}>
            <Logo
              src={blackLogo}
              alt="SOR SEO Logo"
              onError={(e) => {
                console.error('Image failed to load:', e.currentTarget.src);
                e.currentTarget.src = blackLogo; // Fallback to a default image
              }}
            />
          </FooterLogoLink>

          <Slogan>
            {t('Future-ready tech for')} <br /> {t('scalable impact')}
          </Slogan>
          {/* <Button to={`/${language}/contact`}>{t('Contact us')}</Button> */}
        </div>
        <div>
          <List>
            <ListTitle>{t('WHAT WE DO')}</ListTitle>
            {/* <ListItem onClick={() => handleNavigation('/services')}>{t('Our Services')}</ListItem> */}
            <ListItem>
              <FooterLink to={t('servicesLink')}>
                {t('Our Services')}
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink to={t('ourWorkLink')}>{t('Our Work')}</FooterLink>
            </ListItem>
          </List>
          <List>
            <ListTitle>{t('ABOUT US')}</ListTitle>
            <ListItem>
              <FooterLink to={t('aboutLink')}>{t('About SOR SEO')}</FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink to={t('contactLink')}>{t('Contact')}</FooterLink>
            </ListItem>
            <ListItem>
              <ListAnchor
                href="https://www.linkedin.com/company/sor-seo"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('LinkedIn')}
              </ListAnchor>
            </ListItem>
          </List>
          <List>
            <ListTitle>{t('SOR APP')}</ListTitle>
            <ListItem>
              <FooterLink to={t('accountBasedMarketingLink')}>
                {t('Account-based Marketing (B2B)')}
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink to={t('clientLoginLink')}>
                {t('Client Login')}
              </FooterLink>
            </ListItem>
          </List>
          <List>
            <ListTitle>{t('LEGAL')}</ListTitle>
            <ListItem>
              <FooterLink to={t('privacyPolicyLink')}>
                {t('Privacy Policy')}
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink to={t('termsAndConditionsLink')}>
                {t('Terms & Conditions')}
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink to={t('legalNoticeCookiesLink')}>
                {t('Legal Notice & Cookies')}
              </FooterLink>
            </ListItem>
          </List>
        </div>
      </Container>
      <Wordmark aria-hidden="true">
        <strong>SOR SEO</strong>
        <span>
          {language.startsWith('fr')
            ? 'Des idées fortes. Une exécution sans compromis'
            : 'Sharp thinking. Relentless execution'}
        </span>
      </Wordmark>
    </StyledFooter>
  );
};

export default Footer;
